<script setup lang="ts">
import { ButtonType, CustomButton } from '@/components/common';
import { useFormBuilder } from '@/stores/formBuilder';
import { useStudyStore } from '@/stores/study';
import { DateTime } from 'luxon';
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { FormFieldType } from '@/models/form';
import ProgressSpinner from 'primevue/progressspinner';

const route = useRoute();
const identifier = route.params.formIdentifier;

const studyStore = useStudyStore();
const formBuilderStore = useFormBuilder();

const loader = ref(true);
const printAreaRef = ref<HTMLElement | null>(null);
const printedAt = ref<string>('');

/* eslint-disable indent */
async function initPrintData() {
  loader.value = true;
  await formBuilderStore.getFormDetails(identifier as string);
  await studyStore.getStudy(formBuilderStore.form?.studyId as string);
  printedAt.value = DateTime.now().toFormat('yyyy-LL-dd HH:mm');
  loader.value = false;
}
/* eslint-enable indent */

onMounted(initPrintData);

// Optional: Hidden iframe approach (uncomment to use)
const printForm = () => {
  if (!printAreaRef.value) return;
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument!;
  const clone = printAreaRef.value.cloneNode(true) as HTMLElement;

  // Copy styles
  const fragHead = doc.createElement('head');
  document
    .querySelectorAll('link[rel="stylesheet"]')
    .forEach(l => fragHead.appendChild(l.cloneNode(true)));
  document
    .querySelectorAll('style')
    .forEach(s => fragHead.appendChild(s.cloneNode(true)));
  const title = doc.createElement('title');
  title.textContent = formBuilderStore.form?.title || 'Form';
  fragHead.appendChild(title);

  const fragBody = doc.createElement('body');
  fragBody.appendChild(clone);
  doc.documentElement.replaceChildren(fragHead, fragBody);

  iframe.onload = () => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => iframe.remove(), 1000);
  };
};
</script>

<template>
  <div class="root">
    <div class="no-print print-action-bar">
      <CustomButton text="Print"
        :icon="{ style: 'fas', icon: 'print' }"
        @onClick="printForm"
        :type="ButtonType.OUTLINED"/>
    </div>
    <div class="form-print-loader" v-if="loader">
        <ProgressSpinner strokeWidth="4" />
    </div>
    <div class="print-root" ref="printAreaRef">
      <div class="form-metdata">
        <h4>Study: {{ studyStore.selectedStudy?.title }}</h4>
        <div class="printed-at">Printed: {{ printedAt }}</div>
      </div>

      <div class="form-data">
        <h2>{{ formBuilderStore.form?.title }}</h2>

        <div class="fields">
          <div class="field-item"
            v-for="field in formBuilderStore.form?.items"
            :key="field.id">
            <!-- Title / Section -->
            <template v-if="field.type === FormFieldType.TITLE">
              <h2 class="print-title">{{ field.label }}</h2>
              <div v-if="field.description" class="print-description">
                {{ field.description }}
              </div>
            </template>
            <template v-else-if="field.type === FormFieldType.SECTION">
              <h3 class="print-section">{{ field.label }}</h3>
              <div v-if="field.description" class="print-description">
                {{ field.description }}
              </div>
            </template>
            <!-- Text -->
            <template v-else-if="field.type === FormFieldType.TEXT">
              <h4 class="print-label">
                {{ field.label }}
                <span class="req-mark"
                  v-if="field.strictRequired || field.nonStrictRequired">*</span>
                <span class="opt-mark"
v-else-if="field.optional">(Optional)</span>
                <span class="rec-mark"
v-else-if="field.recommended">(Rec)</span>
              </h4>
              <div class="print-multiline">
                <div class="print-line" v-for="n in 3" :key="n"></div>
              </div>
            </template>
            <!-- Number (single or range) -->
            <template v-else-if="field.type === FormFieldType.NUMBER">
              <h4 class="print-label">
                {{ field.label }}
                <span class="req-mark"
                  v-if="field.strictRequired || field.nonStrictRequired">*</span>
                <span class="opt-mark"
v-else-if="field.optional">(Optional)</span>
                <span class="rec-mark"
v-else-if="field.recommended">(Rec)</span>
              </h4>
              <div v-if="field.description" class="print-description">
                {{ field.description }}
              </div>
              <div v-if="field.options.range" class="print-number-range">
                <div class="print-line short"></div>
                <span class="range-separator">to</span>
                <div class="print-line short"></div>
              </div>
              <div v-else class="print-line short"></div>
            </template>
            <!-- Choice -->
            <template v-else-if="field.type === FormFieldType.CHOICE">
              <h4 class="print-label">
                {{ field.label }}
                <span class="req-mark"
                  v-if="field.strictRequired || field.nonStrictRequired">*</span>
                <span class="opt-mark"
v-else-if="field.optional">(Optional)</span>
                <span class="rec-mark"
v-else-if="field.recommended">(Rec)</span>
              </h4>
              <div v-if="field.description" class="print-description">
                {{ field.description }}
              </div>
              <div class="print-choice-group"
                :class="{
                  'single-choice': field.options.maximumChoicesCount === 1,
                }">
                <span v-for="choice in field.options.choices"
                  :key="choice.id"
                  class="print-choice">
                  <span class="print-choice-box"
                    :class="{ radio: field.options.maximumChoicesCount === 1 }"></span>
                  <span class="print-choice-label">{{ choice.label }}</span>
                </span>
                <div v-if="field.options.allowOther" class="print-choice-other">
                  <span class="print-choice-box"
                    :class="{ radio: field.options.maximumChoicesCount === 1 }"></span>
                  <span class="print-choice-label">Other:</span>
                  <div class="print-line other-line"></div>
                </div>
              </div>
            </template>
            <!-- Dropdown -->
            <template v-else-if="field.type === FormFieldType.DROPDOWN">
              <h4 class="print-label">
                {{ field.label }}
                <span class="req-mark"
                  v-if="field.strictRequired || field.nonStrictRequired">*</span>
                <span class="opt-mark"
v-else-if="field.optional">(Optional)</span>
                <span class="rec-mark"
v-else-if="field.recommended">(Rec)</span>
              </h4>
              <div v-if="field.description" class="print-description">
                {{ field.description }}
              </div>
              <div class="print-choice-group single-choice">
                <span v-for="choice in field.options.choices"
                  :key="choice.id"
                  class="print-choice">
                  <span class="print-choice-box radio"></span>
                  <span class="print-choice-label">{{ choice.label }}</span>
                </span>
                <div v-if="field.options.allowOther" class="print-choice-other">
                  <span class="print-choice-box radio"></span>
                  <span class="print-choice-label">Other:</span>
                  <div class="print-line other-line"></div>
                </div>
              </div>
            </template>
            <!-- Date / Time -->
            <template v-else-if="field.type === FormFieldType.DATE">
              <h4 class="print-label">
                {{ field.label }}
                <span class="req-mark"
                  v-if="field.strictRequired || field.nonStrictRequired">*</span>
                <span class="opt-mark"
v-else-if="field.optional">(Optional)</span>
                <span class="rec-mark"
v-else-if="field.recommended">(Rec)</span>
              </h4>
              <div v-if="field.description" class="print-description">
                {{ field.description }}
              </div>
              <div class="print-date-line">
                <div class="date-line"
                  v-if="
                    field.options.mode === 'date' ||
                    field.options.mode === 'datetime'
                  ">
                  <span class="u-box"></span>
                  <span class="u-box"></span>
                  <span class="slash">/</span>
                  <span class="u-box"></span>
                  <span class="u-box"></span>
                  <span class="slash">/</span>
                  <span class="u-box"></span>
                  <span class="u-box"></span>
                  <span class="u-box"></span>
                  <span class="u-box"></span>
                </div>
                <div v-if="
                    field.options.mode === 'time' ||
                    field.options.mode === 'datetime'
                  "
                  class="time-line">
                  <span class="u-box"></span>
                  <span class="u-box"></span>
                  <span class="colon">:</span>
                  <span class="u-box"></span>
                  <span class="u-box"></span>
                </div>
              </div>
            </template>
            <!-- Location -->
            <template v-else-if="field.type === FormFieldType.LOCATION">
              <h4 class="print-label">
                {{ field.label }}
                <span class="req-mark"
                  v-if="field.strictRequired || field.nonStrictRequired">*</span>
                <span class="opt-mark"
v-else-if="field.optional">(Optional)</span>
                <span class="rec-mark"
v-else-if="field.recommended">(Rec)</span>
              </h4>
              <div v-if="field.description" class="print-description">
                {{ field.description }}
              </div>
              <div class="location-lines">
                <div class="loc-row">
                  <span>Lat:</span>
                  <div class="print-line short"></div>
                </div>
                <div class="loc-row">
                  <span>Long:</span>
                  <div class="print-line short"></div>
                </div>
              </div>
            </template>
            <!-- Slider -->
            <template v-else-if="field.type === FormFieldType.SLIDER">
              <h4 class="print-label">
                {{ field.label }}
                <span class="req-mark"
                  v-if="field.strictRequired || field.nonStrictRequired">*</span>
                <span class="opt-mark"
v-else-if="field.optional">(Optional)</span>
                <span class="rec-mark"
v-else-if="field.recommended">(Rec)</span>
              </h4>
              <div v-if="field.description" class="print-description">
                {{ field.description }}
              </div>
              <div class="print-slider">
                <span class="print-slider-min">
                  {{
                    field.options.range?.rangeStart ?? field.options.startValue
                  }}
                </span>
                <span class="print-slider-bar"></span>
                <span class="print-slider-max">
                  {{ field.options.range?.rangeEnd ?? field.options.endValue }}
                </span>
              </div>
              <div class="slider-value-line">
                Value:
                <div class="print-line inline short"></div>
              </div>
            </template>
            <!-- Barcode -->
            <template v-else-if="field.type === FormFieldType.BARCODE">
              <h4 class="print-label">
                {{ field.label }}
                <span class="req-mark"
                  v-if="field.strictRequired || field.nonStrictRequired">*</span>
                <span class="opt-mark"
v-else-if="field.optional">(Optional)</span>
                <span class="rec-mark"
v-else-if="field.recommended">(Rec)</span>
              </h4>
              <div v-if="field.description" class="print-description">
                {{ field.description }}
              </div>
              <div class="print-line"></div>
            </template>
            <!-- Signature -->
            <template v-else-if="field.type === FormFieldType.SIGNATURE">
              <h4 class="print-label">
                {{ field.label }}
                <span class="req-mark"
                  v-if="field.strictRequired || field.nonStrictRequired">*</span>
                <span class="opt-mark"
v-else-if="field.optional">(Optional)</span>
                <span class="rec-mark"
v-else-if="field.recommended">(Rec)</span>
              </h4>
              <div v-if="field.description" class="print-description">
                {{ field.description }}
              </div>
              <div class="print-signature-box"></div>
            </template>
            <!-- File -->
            <template v-else-if="field.type === FormFieldType.FILE">
              <h4 class="print-label">
                {{ field.label }}
                <span class="req-mark"
                  v-if="field.strictRequired || field.nonStrictRequired">*</span>
                <span class="opt-mark"
v-else-if="field.optional">(Optional)</span>
                <span class="rec-mark"
v-else-if="field.recommended">(Rec)</span>
              </h4>
              <div v-if="field.description" class="print-description">
                {{ field.description }}
              </div>
              <div class="print-file-box">
                <div class="file-line" v-for="i in 3" :key="i">
                  File {{ i }}:
                  <div class="print-line inline"></div>
                </div>
              </div>
            </template>
            <!-- Unit -->
            <template v-else-if="field.type === FormFieldType.UNIT">
              <h4 class="print-label">
                {{ field.label }}
                <span class="req-mark"
                  v-if="field.strictRequired || field.nonStrictRequired">*</span>
                <span class="opt-mark"
v-else-if="field.optional">(Optional)</span>
                <span class="rec-mark"
v-else-if="field.recommended">(Rec)</span>
              </h4>
              <div v-if="field.description" class="print-description">
                {{ field.description }}
              </div>
              <div class="unit-wrapper">
                <div class="value-line">
                  Value:
                  <div class="print-line inline short"></div>
                </div>
                <div class="unit-choices"
                  v-if="field.options.units?.list?.length">
                  <span v-for="u in field.options.units.list"
                    :key="u.id"
                    class="unit-choice">
                    <span class="print-choice-box radio"></span>{{ u.unit }}
                  </span>
                </div>
                <div class="inequality-choices"
                  v-if="field.options.choices?.length">
                  <span class="unit-choice"
                    v-for="c in field.options.choices.filter(
                      (c) => c.value !== 'clear'
                    )"
                    :key="c.id">
                    <span class="print-choice-box radio"></span>{{ c.label }}
                  </span>
                </div>
              </div>
            </template>
            <!-- Matrix -->
            <template v-else-if="field.type === FormFieldType.MATRIX">
              <h4 class="print-label">
                {{ field.label }}
                <span class="req-mark"
                  v-if="field.strictRequired || field.nonStrictRequired">*</span>
                <span class="opt-mark"
v-else-if="field.optional">(Optional)</span>
                <span class="rec-mark"
v-else-if="field.recommended">(Rec)</span>
              </h4>
              <div v-if="field.description" class="print-description">
                {{ field.description }}
              </div>
              <div class="matrix-wrapper">
                <table class="print-matrix-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th v-for="col in field.options.columns" :key="col.id">
                        {{ col.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in field.options.rows" :key="row.id">
                      <th>{{ row.label }}</th>
                      <td v-for="col in field.options.columns" :key="col.id">
                        <div class="matrix-cell-blank" :class="col.type"></div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
            <!-- Drilldown -->
            <template v-else-if="field.type === FormFieldType.DRILLDOWN">
              <h4 class="print-label">
                {{ field.label }}
                <span class="req-mark"
                  v-if="field.strictRequired || field.nonStrictRequired">*</span>
                <span class="opt-mark"
v-else-if="field.optional">(Optional)</span>
                <span class="rec-mark"
v-else-if="field.recommended">(Rec)</span>
              </h4>
              <div v-if="field.description" class="print-description">
                {{ field.description }}
              </div>
              <div class="drilldown-levels"
                v-if="field.options.drillLevels?.length">
                <div class="drilldown-level"
                  v-for="(lvl, i) in field.options.drillLevels"
                  :key="i">
                  <span class="lvl-label">{{ lvl.label || "Level " + (i + 1) }}:</span>
                  <div class="print-line inline"></div>
                </div>
              </div>
              <div v-else class="drilldown-levels">
                <div class="drilldown-level">
                  <span class="lvl-label">Value:</span>
                  <div class="print-line inline"></div>
                </div>
              </div>
            </template>
            <!-- Default fallback -->
            <template v-else>
              <h4 class="print-label">
                {{ field.label }}
                <span class="req-mark"
                  v-if="field.strictRequired || field.nonStrictRequired">*</span>
                <span class="opt-mark"
v-else-if="field.optional">(Optional)</span>
                <span class="rec-mark"
v-else-if="field.recommended">(Rec)</span>
              </h4>
              <div v-if="field.description" class="print-description">
                {{ field.description }}
              </div>
              <div class="print-line"></div>
            </template>
            <!-- description blocks moved directly under labels for each field type -->
            <div class="page-break"
              v-if="field.type === FormFieldType.SECTION"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.print-action-bar {
  margin-bottom: 12px;
}
.print-root {
  padding: 16px;
  font-family: system-ui, sans-serif;
  color: #000;
}
.printed-at {
  font-size: 12px;
  color: #444;
}

h2.print-title {
  margin: 12px 0 4px;
  font-size: 22px;
  border-bottom: 2px solid #000;
  padding-bottom: 4px;
}
h3.print-section {
  margin: 32px 0 8px;
  font-size: 18px;
  background: #f2f2f2;
  padding: 6px 10px;
  border: 1px solid #000;
}

.fields {
  margin-top: 12px;
}
.field-item {
  margin-bottom: 20px;
  page-break-inside: avoid;
}
.print-label {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 4px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.req-mark {
  color: #c00;
}
.opt-mark,
.rec-mark {
  font-weight: normal;
  font-size: 11px;
  color: #555;
}

.print-description {
  font-size: 11px;
  color: #333;
  margin-top: 4px;
}

/* Generic line */
.print-line {
  border-bottom: 1px solid #000;
  width: 100%;
  height: 18px;
  &.short {
    width: 180px;
  }
  &.inline {
    display: inline-block;
    vertical-align: middle;
  }
}
.print-multiline {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Number range */
.print-number-range {
  display: flex;
  align-items: center;
  gap: 8px;
  .range-separator {
    font-size: 12px;
  }
}

/* Choice / Dropdown */
.print-choice-group {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: center;
}
.print-choice {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}
.print-choice-box {
  width: 14px;
  height: 14px;
  border: 1px solid #000;
  display: inline-block;
  border-radius: 2px;
  &.radio {
    border-radius: 50%;
  }
}
.print-choice-other {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  margin-top: 4px;
  .other-line {
    flex: 1;
  }
}
.print-dropdown-line {
  display: flex;
  align-items: center;
  gap: 8px;
  .dropdown-indicator {
    font-size: 12px;
    margin-top: -2px;
  }
}

/* Date / Time */
.print-date-wrapper {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 6px;
  .date-group,
  .time-segment {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    label {
      font-size: 10px;
      letter-spacing: 0.5px;
    }
  }
  .char-boxes {
    display: flex;
    gap: 2px;
  }
  .char-box {
    width: 16px;
    height: 20px;
    border: 1px solid #000;
  }
  .separator {
    font-size: 16px;
    line-height: 16px;
    padding: 0 2px;
  }
}

/* Location */
.location-lines {
  display: flex;
  flex-direction: column;
  gap: 4px;
  .loc-row {
    display: flex;
    align-items: center;
    gap: 6px;
    span {
      font-size: 12px;
      width: 34px;
    }
    .print-line {
      flex: 1;
    }
  }
}

/* New date placeholder styles */
.print-date-line {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.date-line,
.time-line {
  display: flex;
  align-items: center;
  gap: 6px;
}
.date-segment {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}
.seg-label {
  font-size: 10px;
  font-weight: 600;
}
.u-box {
  width: 10px;
  border-bottom: 1px solid #000;
  height: 14px;
  display: inline-block;
}
.time-line .u-box {
  width: 10px;
}
.slash,
.colon {
  font-weight: 600;
  font-size: 12px;
}

/* Slider */
.print-slider {
  display: flex;
  align-items: center;
  gap: 10px;
  .print-slider-bar {
    flex: 1;
    height: 4px;
    background: repeating-linear-gradient(
      90deg,
      #000 0,
      #000 2px,
      transparent 2px,
      transparent 10px
    );
    position: relative;
  }
  .print-slider-min,
  .print-slider-max {
    font-size: 11px;
    width: 40px;
    text-align: center;
  }
}
.slider-value-line {
  margin-top: 4px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Signature */
.print-signature-box {
  border: 1px solid #000;
  height: 80px;
  margin-top: 4px;
  position: relative;
}

/* File */
.print-file-box {
  display: flex;
  flex-direction: column;
  gap: 6px;
  .file-line {
    display: flex;
    align-items: center;
    gap: 6px;
    .print-line {
      flex: 1;
    }
  }
}

/* Unit */
.unit-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
  .value-line {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .unit-choices,
  .inequality-choices {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }
  .unit-choice {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
  }
}

/* Matrix */
.matrix-wrapper {
  overflow: auto;
}
.print-matrix-table {
  border-collapse: collapse;
  width: 100%;
  font-size: 11px;
  table-layout: fixed;
  th,
  td {
    border: 1px solid #000;
    padding: 4px;
    text-align: center;
  }
  th {
    background: #f4f4f4;
    font-weight: 600;
  }
  .matrix-cell-blank {
    width: 100%;
    height: 18px;
    &.Number {
      text-align: right;
    }
  }
}

/* Drilldown */
.drilldown-levels {
  display: flex;
  flex-direction: column;
  gap: 6px;
  .drilldown-level {
    display: flex;
    align-items: center;
    gap: 6px;
    .lvl-label {
      font-size: 12px;
      width: 70px;
    }
    .print-line {
      flex: 1;
    }
  }
}

/* Page breaks */
.page-break {
  page-break-after: avoid;
}

@media print {
  .no-print {
    display: none !important;
  }
  body,
  .print-root {
    background: #fff;
  }
  .field-item {
    break-inside: avoid;
  }
  h2.print-title {
    page-break-after: avoid;
  }
}
</style>
